import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { forceSimulation, forceManyBody, forceLink, forceCenter, forceCollide } from "d3-force";

import dagre from "dagre";
import {
    ReactFlow,
    Background,
    Controls,
    Handle,
    Position,
    useNodesState,
    useEdgesState,
    MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import FloatingEdge from "../FloatingEdge"; // <-- popraw ścieżkę jeśli trzeba
import ReadOnlyWorkflowViewer from "./ProcessDetailsFlowDialog";

// ====== KONFIG ======
const ACCENT = "#774CBC";
const nodeWidth = 240;
const nodeHeight = 92;

function clamp(v, a, b) {
    return Math.max(a, Math.min(b, v));
}

// #RRGGBB -> [r,g,b]
function hexToRgb(hex) {
    const h = String(hex).replace("#", "").trim();
    const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    const n = parseInt(full, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgba(hex, a) {
    const [r, g, b] = hexToRgb(hex);
    return `rgba(${r},${g},${b},${a})`;
}

function dagreLayout(nodes, edges, opts = {}) {
    const { rankdir = "TB", nodesep = 90, ranksep = 140 } = opts;

    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir, nodesep, ranksep });

    nodes.forEach((n) => {
        const w = n?.style?.width ?? nodeWidth;
        const h = n?.style?.height ?? nodeHeight;
        g.setNode(String(n.id), { width: w, height: h });
    });

    edges.forEach((e) => g.setEdge(String(e.source), String(e.target)));

    dagre.layout(g);

    return nodes.map((n) => {
        const p = g.node(String(n.id));
        const w = n?.style?.width ?? nodeWidth;
        const h = n?.style?.height ?? nodeHeight;
        return {
            ...n,
            position: { x: p.x - w / 2, y: p.y - h / 2 },
        };
    });
}

function forceLayout(nodes, edges, opts = {}) {
    const {
        iterations = 300,
        linkDistance = 220,
        chargeStrength = -1200,
        collidePadding = 26,
    } = opts;

    // d3 pracuje na obiektach które ma prawo mutować
    const simNodes = nodes.map((n) => {
        const w = n?.style?.width ?? nodeWidth;
        const h = n?.style?.height ?? nodeHeight;
        return {
            id: String(n.id),
            x: Number.isFinite(n.position?.x) ? n.position.x : 0,
            y: Number.isFinite(n.position?.y) ? n.position.y : 0,
            w,
            h,
        };
    });

    const nodeById = new Map(simNodes.map((n) => [n.id, n]));

    const simLinks = edges
        .filter((e) => nodeById.has(String(e.source)) && nodeById.has(String(e.target)))
        .map((e) => ({
            source: String(e.source),
            target: String(e.target),
        }));

    const sim = forceSimulation(simNodes)
        .force("charge", forceManyBody().strength(chargeStrength))
        .force("link", forceLink(simLinks).id((d) => d.id).distance(linkDistance).strength(0.15))
        .force("center", forceCenter(0, 0))
        // 🔥 kolizje — to jest klucz żeby NIC się nie nakładało
        .force(
            "collide",
            forceCollide((d) => Math.max(d.w, d.h) / 2 + collidePadding).iterations(2)
        )
        .stop();

    // ręczne “ticki” — deterministyczne i szybkie
    for (let i = 0; i < iterations; i++) sim.tick();

    // map back do ReactFlow nodes (RF chce top-left)
    const out = nodes.map((n) => {
        const sn = nodeById.get(String(n.id));
        if (!sn) return n;

        const w = n?.style?.width ?? nodeWidth;
        const h = n?.style?.height ?? nodeHeight;

        return {
            ...n,
            position: {
                x: sn.x - w / 2,
                y: sn.y - h / 2,
            },
        };
    });

    return out;
}

// ====== FETCH ======
async function fetchRobots({ token, category }) {
    const query = `api/getrobots?is_active=1&category=${category}`;
    const res = await fetch(query, {
        headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
        },
    });
    const json = await res.json();
    return Array.isArray(json?.results) ? json.results : [];
}


function deleteNodeFromGraph(flowData, nodeId) {
    const f = ensureFlowShape(flowData);

    const id = normalizeId(nodeId);

    // 1) usuń node
    f.nodes = (f.nodes ?? []).filter((n) => normalizeId(n.id) !== id);

    // 2) usuń wszystkie edge'e które go dotykają
    f.edges = (f.edges ?? []).filter((e) => {
        const s = normalizeId(e.source);
        const t = normalizeId(e.target);
        return s !== id && t !== id;
    });

    // 3) jeśli focus był na tym node => wróć na Agent
    f.ui = f.ui ?? {};
    if (normalizeId(f.ui.focusNodeId) === id) f.ui.focusNodeId = "Agent";

    return f;
}

// ====== HELPERS ======
function deepClone(x) {
    return JSON.parse(JSON.stringify(x));
}

function ensureFlowShape(flowData) {
    const base = flowData ?? {};
    return {
        version: base.version ?? 1,
        agent: base.agent ?? { id: "Agent", label: "Agent" },
        nodes: Array.isArray(base.nodes) ? base.nodes : [],
        edges: Array.isArray(base.edges) ? base.edges : [],
        ui: base.ui ?? { focusNodeId: "Agent" },
    };
}

function normalizeId(x) {
    return String(x ?? "").trim();
}


// forbidden list for picker:
// - self (parent)
// - already connected targets from parent
// - (optional) block ancestors/cycles: for now we block only direct duplicates + self (simple & safe)
function forbiddenForGraph(flowData, menu) {
    const flow = ensureFlowShape(flowData);
    const forbidden = new Set();

    const parentId = menu.mode === "root" ? "Agent" : normalizeId(menu.parentNodeId);
    forbidden.add(parentId); // cannot add self

    for (const e of flow.edges) {
        const s = normalizeId(e.source);
        const t = normalizeId(e.target);

        // Do not show items already connected from this parent
        if (s === parentId) forbidden.add(t);

        // If you treat bidirectional as "already related", also block reverse selection
       /* if (e.bidirectional && t === parentId) forbidden.add(s);*/
    }

    return forbidden;
}



function upsertNode(flow, node) {
    const id = normalizeId(node.id);
    const idx = flow.nodes.findIndex((n) => normalizeId(n.id) === id);
    if (idx === -1) flow.nodes.push(node);
    else flow.nodes[idx] = { ...flow.nodes[idx], ...node };
}

function upsertEdge(flow, edge) {
    const s = normalizeId(edge.source);
    const t = normalizeId(edge.target);

    // unique by (source,target)
    const idx = flow.edges.findIndex(
        (e) => normalizeId(e.source) === s && normalizeId(e.target) === t
    );
    if (idx === -1) flow.edges.push(edge);
    else flow.edges[idx] = { ...flow.edges[idx], ...edge };
}

// ====== NODE ======
function CustomNode({ data }) {
    const isAgent = data?.kind === "AGENT";
    const [hover, setHover] = useState(false);
    const deg = Number(data?.degree ?? 0);

    // mapowanie: 0..maxDeg -> 0..1
    const maxDeg = 8; // dopasuj pod “typowy” graf
    const t = clamp(deg / maxDeg, 0, 1);

    // intensywność akcentu
    const borderA = 0.25 + 0.45 * t;   // 0.25..0.70
    const glowA = 0.06 + 0.22 * t;   // 0.06..0.28
    const tintA = 0.02 + 0.10 * t;   // 0.02..0.12


    // --- tiny inline SVG icons (no text-emoji) ---
    const Icon = {
        Plus: (props) => (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" {...props}>
                <path
                    d="M12 5v14M5 12h14"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                />
            </svg>
        ),
        Trash: (props) => (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" {...props}>
                <path
                    d="M9 3h6m-8 4h10m-9 0 .8 13.2A2 2 0 0 0 9.8 22h4.4a2 2 0 0 0 2-1.8L17 7"
                    stroke="currentColor"
                    strokeWidth="2.1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M10 11v7M14 11v7"
                    stroke="currentColor"
                    strokeWidth="2.1"
                    strokeLinecap="round"
                />
            </svg>
        ),
        Eye: (props) => (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" {...props}>
                <path
                    d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z"
                    stroke="currentColor"
                    strokeWidth="2.1"
                    strokeLinejoin="round"
                />
                <path
                    d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                    stroke="currentColor"
                    strokeWidth="2.1"
                />
            </svg>
        ),
        Agent: (props) => (
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" {...props}>
                <path
                    d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.5 4.5 0 0 0 12 12Z"
                    fill="rgba(255,255,255,0.95)"
                />
                <path
                    d="M4.5 21a7.5 7.5 0 0 1 15 0"
                    stroke="rgba(255,255,255,0.95)"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                />
            </svg>
        ),
    };

    const stop = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    if (isAgent) {
        return (
            <div
                style={{
                    pointerEvents: "all",
                    width: 92,
                    height: 92,
                    borderRadius: 999,
                    border: "4px solid rgba(255,255,255,0.92)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    boxShadow:
                        "0 20px 55px rgba(17,24,39,0.28), inset 0 0 0 1px rgba(255,255,255,0.16)",
                    background:
                        "radial-gradient(120px 120px at 30% 25%, rgba(255,255,255,0.20), rgba(255,255,255,0) 55%), linear-gradient(135deg, #6366f1 0%, #7c3aed 55%, #9333ea 100%)",
                    fontSize: 12,
                    fontWeight: 800,
                    position: "relative",
                    userSelect: "none",
                }}
            >
                <div style={{ lineHeight: 1, marginBottom: 4 }}>
                    <Icon.Agent />
                </div>
                <div
                    style={{
                        fontSize: 11,
                        letterSpacing: 0.2,
                        opacity: 0.95,
                        fontWeight: 800,
                    }}
                >
                    Agent
                </div>

                {/* Add root */}
                <button
                    onPointerDown={stop}
                    onMouseDown={stop}
                    onClick={(e) => {
                        stop(e);
                        data?.onOpenMenu?.(e, "root");
                    }}
                    title="Add"
                    style={{
                        position: "absolute",
                        left: "50%",
                        bottom: -16,
                        transform: "translateX(-50%)",
                        width: 30,
                        height: 30,
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.55)",
                        background:
                            "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,255,255,0.86))",
                        boxShadow: "0 14px 28px rgba(0,0,0,0.22)",
                        cursor: "pointer",
                        color: ACCENT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Icon.Plus />
                </button>

                <Handle type="source" position={Position.Bottom} style={{ display: "none" }} />
            </div>
        );
    }

    const label = data?.label ?? "Unnamed";

    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                pointerEvents: "all",
                width: nodeWidth,
                minHeight: nodeHeight,
                borderRadius: 999,
                border: `1px solid ${rgba(ACCENT, borderA)}`,
                background: `linear-gradient(180deg, rgba(255,255,255,0.98), ${rgba(ACCENT, tintA)})`,
                boxShadow: hover
                    ? `0 18px 44px rgba(17,24,39,0.18), 0 0 0 3px ${rgba(ACCENT, glowA)}`
                    : `0 14px 34px rgba(17,24,39,0.14), 0 0 0 3px ${rgba(ACCENT, glowA * 0.75)}`,
                padding: "14px 18px",
                textAlign: "left",
                fontFamily: "system-ui, -apple-system, Segoe UI, Roboto",
                position: "relative",
                userSelect: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                transform: hover ? "translateY(-1px)" : "translateY(0)",
                transition: "box-shadow 140ms ease, transform 140ms ease",
            }}
        >
            {deg > 0 && (
                <div
                    style={{
                        position: "absolute",
                        left: 14,
                        top: -10,
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: rgba(ACCENT, 0.12 + 0.18 * t),
                        border: `1px solid ${rgba(ACCENT, 0.25 + 0.35 * t)}`,
                        fontSize: 11,
                        fontWeight: 950,
                        color: "rgba(15,23,42,0.75)",
                    }}
                    title="Number of connections"
                >
                    {deg}
                </div>
            )}

            {/* Bottom-center: add relation */}
            <button
                onPointerDown={stop}
                onMouseDown={stop}
                onClick={(e) => {
                    stop(e);
                    data?.onOpenMenu?.(e, "child");
                }}
                title="Add relation"
                style={{
                    position: "absolute",
                    left: "50%",
                    bottom: -22,
                    transform: "translateX(-50%)",
                    width: 48,
                    height: 48,
                    borderRadius: 999,
                    border: `1px solid ${hover ? "rgba(119,76,188,0.55)" : "rgba(17,24,39,0.12)"}`,
                    background: hover
                        ? "linear-gradient(180deg, rgba(119,76,188,0.14), rgba(255,255,255,0.92))"
                        : "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,255,255,0.88))",
                    boxShadow: "0 16px 30px rgba(17,24,39,0.18)",
                    cursor: "pointer",
                    color: ACCENT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: hover ? 1 : 0.7,
                    transition: "opacity 140ms ease, background 140ms ease, border 140ms ease",
                }}
            >
                <Icon.Plus />
            </button>

            {/* Top-right: actions (View + Delete) */}
            <div
                style={{
                    position: "absolute",
                    top: -12,
                    right: -12,
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                }}
            >
                {/* View */}
                <button
                    onPointerDown={stop}
                    onMouseDown={stop}
                    onClick={(e) => {
                        stop(e);
                        data?.onShowDetails?.();
                    }}
                    title="View details"
                    style={{
                        height: 28,
                        padding: "0 12px",
                        borderRadius: 999,
                        border: `1px solid ${hover ? "rgba(119,76,188,0.45)" : "rgba(17,24,39,0.12)"}`,
                        background: hover
                            ? "linear-gradient(180deg, rgba(119,76,188,0.10), rgba(255,255,255,0.95))"
                            : "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,255,255,0.88))",
                        boxShadow: "0 14px 24px rgba(17,24,39,0.16)",
                        cursor: "pointer",
                        color: "#111827",
                        fontWeight: 900,
                        fontSize: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        opacity: hover ? 1 : 0.55,
                        transition:
                            "opacity 140ms ease, background 140ms ease, border 140ms ease, transform 140ms ease",
                        transform: hover ? "translateY(-0.5px)" : "translateY(0)",
                    }}
                >
                    <Icon.Eye style={{ color: ACCENT }} />
                    <span style={{ letterSpacing: 0.2 }}>View</span>
                </button>

                {/* Delete */}
                <button
                    onPointerDown={stop}
                    onMouseDown={stop}
                    onClick={(e) => {
                        stop(e);
                        data?.onDelete?.();
                    }}
                    title="Delete"
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: `1px solid ${hover ? "rgba(239,68,68,0.35)" : "rgba(17,24,39,0.12)"}`,
                        background: hover
                            ? "linear-gradient(180deg, rgba(239,68,68,0.10), rgba(255,255,255,0.95))"
                            : "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,255,255,0.88))",
                        boxShadow: "0 14px 24px rgba(17,24,39,0.16)",
                        cursor: "pointer",
                        color: hover ? "rgba(239,68,68,0.95)" : "rgba(239,68,68,0.78)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: hover ? 1 : 0.55,
                        transition: "opacity 140ms ease, background 140ms ease, border 140ms ease",
                    }}
                >
                    <Icon.Trash />
                </button>
            </div>

            {/* Main content */}
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <div
                    style={{
                        fontWeight: 950,
                        color: "#0f172a",
                        fontSize: 14,
                        lineHeight: 1.2,
                        maxWidth: 190,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        letterSpacing: 0.1,
                    }}
                    title={label}
                >
                    {label}
                </div>

                {/* optional subtle subline placeholder (keeps it premium, but empty-safe) */}
                <div
                    style={{
                        marginTop: 4,
                        fontSize: 12,
                        color: "rgba(15,23,42,0.55)",
                        fontWeight: 650,
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {data?.category ? String(data.category).toUpperCase() : ""}
                </div>
            </div>

            <Handle type="target" position={Position.Top} style={{ display: "none" }} />
            <Handle type="source" position={Position.Bottom} style={{ display: "none" }} />
        </div>
    );
}





const nodeTypes = { custom: CustomNode };
const edgeTypes = { floating: FloatingEdge };

function BubbleCloudOverlay({ open, text = "Generating…" }) {
    if (!open) return null;

    // deterministic particles
    const count = 34;
    const particles = Array.from({ length: count }).map((_, i) => {
        // 0..1
        const t = (i + 1) / (count + 1);

        // radius grows outward (spiral)
        const r = 30 + t * 260; // px
        const size = 10 + (i % 7) * 4 + Math.floor(t * 10); // px-ish
        const speed = 2.8 + (i % 8) * 0.25; // seconds
        const delay = (i % 12) * -0.18; // negative => starts mid-motion
        const alpha = 0.12 + t * 0.22;

        // distribute angles around circle (with small jitter)
        const angle = (i * 360) / count + ((i % 5) - 2) * 6;

        // depth parallax
        const depth = 0.35 + (i % 9) * 0.07; // affects blur + size scale a bit

        return { key: i, r, size, speed, delay, alpha, angle, depth };
    });

    return (
        <div
            style={{
                position: "absolute",
                inset: 0,
                zIndex: 140,
                borderRadius: 14,
                overflow: "hidden",
                background:
                    "radial-gradient(900px 520px at 35% 25%, rgba(255,255,255,0.94), rgba(255,255,255,0.70) 55%, rgba(255,255,255,0.30) 100%), linear-gradient(180deg, rgba(17,24,39,0.58), rgba(17,24,39,0.36))",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "rfFadeIn 200ms ease-out",
            }}
        >
            <style>
                {`
          @keyframes rfFadeIn {
            from { opacity: 0; transform: scale(1.012); }
            to   { opacity: 1; transform: scale(1); }
          }

          /* Whole vortex rotation (slow, premium) */
          @keyframes rfVortexSpin {
            0%   { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
          }

          /* Each particle orbits + subtle breathing */
          @keyframes rfOrbit {
            0%   { transform: rotate(var(--a)) translateX(var(--r)) rotate(calc(-1 * var(--a))) scale(0.96); }
            50%  { transform: rotate(calc(var(--a) + 180deg)) translateX(calc(var(--r) * 1.02)) rotate(calc(-1 * (var(--a) + 180deg))) scale(1.06); }
            100% { transform: rotate(calc(var(--a) + 360deg)) translateX(var(--r)) rotate(calc(-1 * (var(--a) + 360deg))) scale(0.96); }
          }

          /* Spiral pull: very subtle radial breathing to fake "vortex" depth */
          @keyframes rfSpiral {
            0%   { filter: blur(var(--blur)); opacity: var(--o); }
            50%  { filter: blur(calc(var(--blur) * 0.75)); opacity: calc(var(--o) + 0.08); }
            100% { filter: blur(var(--blur)); opacity: var(--o); }
          }

          /* Soft aurora haze drifting like Vista-ish */
          @keyframes rfAurora {
            0%   { transform: translate3d(-2%, -2%, 0) scale(1.02); opacity: 0.75; }
            50%  { transform: translate3d(2%, 1%, 0) scale(1.05); opacity: 0.92; }
            100% { transform: translate3d(-2%, -2%, 0) scale(1.02); opacity: 0.75; }
          }

          /* tiny shimmer line */
          @keyframes rfShimmer {
            0%   { transform: translateX(-40%) }
            50%  { transform: translateX(40%) }
            100% { transform: translateX(-40%) }
          }
        `}
            </style>

            {/* Aurora background haze */}
            <div
                style={{
                    position: "absolute",
                    inset: -120,
                    pointerEvents: "none",
                    background:
                        "radial-gradient(650px 420px at 30% 40%, rgba(119,76,188,0.26), rgba(255,255,255,0) 62%), radial-gradient(620px 420px at 70% 55%, rgba(99,102,241,0.18), rgba(255,255,255,0) 62%), radial-gradient(720px 520px at 55% 20%, rgba(147,51,234,0.14), rgba(255,255,255,0) 60%)",
                    filter: "blur(14px) saturate(1.12)",
                    animation: "rfAurora 5.6s ease-in-out infinite",
                    mixBlendMode: "screen",
                }}
            />

            {/* Vortex container */}
            <div
                style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: 740,
                    height: 520,
                    transform: "translate(-50%, -50%)",
                    pointerEvents: "none",
                }}
            >
                {/* The entire field rotates slowly */}
                <div
                    style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: "100%",
                        height: "100%",
                        transform: "translate(-50%, -50%)",
                        animation: "rfVortexSpin 7.2s linear infinite",
                    }}
                >
                    {particles.map((p) => {
                        const blur = 0.6 + (1 - p.depth) * 2.0; // more blur for "far"
                        const scale = 0.86 + p.depth * 0.36;

                        return (
                            <div
                                key={p.key}
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    top: "50%",
                                    width: p.size,
                                    height: p.size,
                                    borderRadius: 999,
                                    transformOrigin: "0 0",
                                    // CSS vars for keyframes
                                    ["--r"]: `${p.r}px`,
                                    ["--a"]: `${p.angle}deg`,
                                    ["--o"]: p.alpha,
                                    ["--blur"]: `${blur}px`,
                                    animation: `rfOrbit ${p.speed}s cubic-bezier(.22,.7,.25,1) ${p.delay}s infinite, rfSpiral ${Math.max(
                                        2.8,
                                        p.speed * 1.15
                                    )}s ease-in-out ${p.delay}s infinite`,
                                    background:
                                        "radial-gradient(circle at 28% 28%, rgba(255,255,255,0.65), rgba(255,255,255,0.0) 36%), radial-gradient(circle at 45% 55%, rgba(119,76,188,0.70), rgba(119,76,188,0.15) 60%, rgba(255,255,255,0.06) 100%)",
                                    boxShadow:
                                        "0 22px 60px rgba(17,24,39,0.18), inset 0 0 0 1px rgba(255,255,255,0.22)",
                                    filter: `saturate(1.05)`,
                                    opacity: p.alpha,
                                    // depth scale
                                    scale: scale,
                                }}
                            />
                        );
                    })}
                </div>

                {/* bright core glow */}
                <div
                    style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: 120,
                        height: 120,
                        transform: "translate(-50%, -50%)",
                        borderRadius: 999,
                        background:
                            "radial-gradient(circle at 40% 35%, rgba(255,255,255,0.55), rgba(255,255,255,0.15) 45%, rgba(119,76,188,0.08) 70%, rgba(255,255,255,0) 100%)",
                        filter: "blur(2px)",
                        opacity: 0.85,
                    }}
                />
            </div>

            {/* Center “Generating” card */}
            <div
                style={{
                    position: "relative",
                    zIndex: 2,
                    width: 520,
                    maxWidth: "88vw",
                    borderRadius: 18,
                    padding: "18px 18px 16px",
                    background:
                        "linear-gradient(180deg, rgba(255,255,255,0.84), rgba(255,255,255,0.60))",
                    border: "1px solid rgba(17,24,39,0.14)",
                    boxShadow: "0 30px 90px rgba(0,0,0,0.22)",
                    textAlign: "center",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 950,
                        letterSpacing: 0.38,
                        color: "rgba(15,23,42,0.76)",
                        textTransform: "uppercase",
                    }}
                >
                    {text}
                </div>

                <div
                    style={{
                        marginTop: 10,
                        height: 2,
                        borderRadius: 999,
                        overflow: "hidden",
                        background: "rgba(15,23,42,0.08)",
                    }}
                >
                    <div
                        style={{
                            height: "100%",
                            width: "44%",
                            borderRadius: 999,
                            background:
                                "linear-gradient(90deg, rgba(99,102,241,0.0), rgba(119,76,188,0.75), rgba(147,51,234,0.0))",
                            animation: "rfShimmer 1.35s ease-in-out infinite",
                        }}
                    />
                </div>

                <div
                    style={{
                        marginTop: 10,
                        fontSize: 12,
                        fontWeight: 650,
                        color: "rgba(15,23,42,0.56)",
                        lineHeight: 1.35,
                    }}
                >
                    Curating the best skills and weaving relations into a clean graph.
                </div>
            </div>
        </div>
    );
}

// ====== COMPONENT ======
export default function AgentFlowCreator({ token, onClose, flowData, setFlowData, jobDescription, aiMode, setAiMode }) {
    const proOptions = { account: "paid-pro", hideAttribution: true };

    const draft = useMemo(() => ensureFlowShape(flowData), [flowData]);

    const [nodes, setNodes] = useNodesState([]);
    const [edges, setEdges] = useEdgesState([]);
    const [draftSaved, setDraftSaved] = useEdgesState(true);

    const [details, setDetails] = useState({ open: false, flowJson: null });

    const [aiGen, setAiGen] = useState({
        open: false,
        phase: "idle", // idle | loading | success | error
        message: "Generating…",
    });



    const stopAiOverlay = useCallback((phase = "success", msg = "") => {
        setAiGen((s) => ({
            open: phase === "loading" ? true : false,
            phase,
            message: msg || s.message,
        }));
    }, []);



    const deleteNode = useCallback((nodeId) => {
        console.log("DELETE:", nodeId);

        setFlowData((prev) => {
            const next = deleteNodeFromGraph(prev ?? {}, nodeId);
            return next;
        });
    }, [setFlowData]);


    // menu (kompaktowe, przy klikniętym plusiku)
    const containerRef = useRef(null);
    const [menu, setMenu] = useState({
        open: false,
        x: 0,
        y: 0,
        mode: "root", // root | child
        parentNodeId: null, // in child: node guid/id
    });

    // picker data
    const [pickerCategory, setPickerCategory] = useState("skill"); // skill | process
    const [pickerResults, setPickerResults] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);

    const rfRef = useRef(null);
    const fitTimer = useRef(null);

    const closeMenu = useCallback(() => setMenu((m) => ({ ...m, open: false })), []);

    const startAiOverlay = useCallback((msg = "Generating…") => {
        // schowaj inne UI, żeby było premium
        closeMenu();
        setAiGen({ open: true, phase: "loading", message: msg });
    }, [closeMenu]);


    const openMenuAtEvent = useCallback((evt, mode, parentNodeId = null) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const x = evt.clientX - rect.left;
        const y = evt.clientY - rect.top;

        setMenu({ open: true, x, y, mode, parentNodeId });
    }, []);

    const loadPicker = useCallback(
        async (category) => {
            setPickerLoading(true);
            try {
                const results = await fetchRobots({ token, category });
                setPickerResults(results);
            } finally {
                setPickerLoading(false);
            }
        },
        [token]
    );

    const clearAll = useCallback(() => {
        closeMenu();
        setFlowData((prev) => {
            const next = ensureFlowShape(deepClone(prev ?? {}));
            next.nodes = [];
            next.edges = [];
            next.ui = { ...(next.ui ?? {}), focusNodeId: "Agent" };
            return next;
        });
    }, [closeMenu, setFlowData]);

    // ====== BUILD VIEW (focus graph -> RF nodes/edges -> dagre layout) ======
    const rebuild = useCallback(
        (flow) => {
            const f = ensureFlowShape(flow);

            const rawNodes = [];
            const rawEdges = [];

            // Agent
            rawNodes.push({
                id: "Agent",
                type: "custom",
                data: {
                    kind: "AGENT",
                    label: "Agent",
                    onOpenMenu: (e, mode) => openMenuAtEvent(e, mode, null),
                },
                style: { width: 92, height: 92 },
                position: { x: 0, y: 0 },
            });
            // AFTER building rawEdges:
            const degree = new Map(); // nodeId -> count


           

            // ALL edges
            for (const e of f.edges) {
                const s = String(e.source);
                const t = String(e.target);
                const relation = String(e.relation ?? "related");

                rawEdges.push({
                    id: `e-${s}->${t}`, // ✅ zawsze safe (source/target u Ciebie są bez ':')
                    source: s,
                    target: t,
                    type: "floating",
                    animated: true,
                    label: relation,
                    style: { stroke: ACCENT, strokeWidth: s === "Agent" ? 4 : 2.4 },
                    markerEnd: { type: MarkerType.ArrowClosed, color: ACCENT },
                    labelBgPadding: [6, 3],
                    labelBgBorderRadius: 6,
                    labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
                });
            }

            for (const e of rawEdges) {
                const s = String(e.source);
                const t = String(e.target);
                degree.set(s, (degree.get(s) ?? 0) + 1);
                degree.set(t, (degree.get(t) ?? 0) + 1);
            }
            // ALL graph nodes
            for (const n of f.nodes) {
                const id = String(n.id);
                const d = degree.get(id) ?? 0;
                rawNodes.push({
                    id,
                    type: "custom",
                    data: {
                        label: n.label ?? n.name ?? "Unnamed",
                        // NIE pokazujemy ROOT/STANDARD już w UI, ale możesz zachować w payloadzie
                        category: n.category,
                        degree: d,
                        // 🔍 szczegóły
                        flowJson: n.data, // <-- to jest data z getrobots, czyli flow json do ReadOnlyWorkflowViewer
                        onShowDetails: () => setDetails({ open: true, flowJson: n.data }),

                        // 🗑️ delete
                        onDelete: () => deleteNode(n.id),

                        // plus menu
                        onOpenMenu: (e) => openMenuAtEvent(e, "child", n.id),
                    },

                    style: { width: nodeWidth, height: nodeHeight },
                    position: { x: 0, y: 0 },
                });
            }


            // jeśli chcesz, żeby Agent też się liczył naturalnie – nic więcej nie trzeba
            // (już jest w rawEdges)

            function normalizePositions(nodes, maxSpan = 2000) {
                let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                for (const n of nodes) {
                    minX = Math.min(minX, n.position.x);
                    minY = Math.min(minY, n.position.y);
                    maxX = Math.max(maxX, n.position.x);
                    maxY = Math.max(maxY, n.position.y);
                }
                const spanX = maxX - minX || 1;
                const spanY = maxY - minY || 1;
                const scale = Math.min(maxSpan / spanX, maxSpan / spanY, 1);

                return nodes.map(n => ({
                    ...n,
                    position: {
                        x: (n.position.x - minX) * scale,
                        y: (n.position.y - minY) * scale,
                    }
                }));
            }


            let layoutedNodes = dagreLayout(rawNodes, rawEdges, {
                rankdir: "TB",    // albo "LR" jak chcesz poziomo
                nodesep: 90,
                ranksep: 160,
            });

            // opcjonalnie: tylko delikatny “polish” force, mało iteracji
            layoutedNodes = forceLayout(layoutedNodes, rawEdges, {
                iterations: 60,
                linkDistance: 120,
                chargeStrength: -400,   // ujemne!
                collidePadding: 40,
            });

            layoutedNodes = normalizePositions(layoutedNodes);

            setNodes(layoutedNodes);
            setEdges(rawEdges);


            if (fitTimer.current) clearTimeout(fitTimer.current);
            fitTimer.current = setTimeout(() => {
                const inst = rfRef.current;
                if (!inst) return;
                inst.fitView({ padding: 0.30, duration: 240, minZoom: 0.18, maxZoom: 1.05 });
            }, 40);
        },
        [openMenuAtEvent, setNodes, setEdges, deleteNode]

    );

    useEffect(() => {
        rebuild(draft);
        console.log("FLOW PAYLOAD:", JSON.stringify(flowData ?? null, null, 2));
        try {
            const f = ensureFlowShape(flowData ?? {});
            const nodeSet = new Set(["Agent", ...(f.nodes ?? []).map((n) => String(n.id))]);

            const badEdgeIds = (f.edges ?? []).filter((e) => /[:]/.test(String(e.id ?? "")));
            const missingRefs = (f.edges ?? []).filter((e) => !nodeSet.has(String(e.source)) || !nodeSet.has(String(e.target)));

            console.log("DIAG nodes:", nodeSet.size);
            console.log("DIAG edges:", (f.edges ?? []).length);
            console.log("DIAG badEdgeIds:", badEdgeIds.map((x) => x.id));
            console.log("DIAG missingRefs:", missingRefs);
        } catch (e) {
            console.error("DIAG crashed:", e);
        }

    }, [draft, rebuild]);

    const onInit = useCallback((instance) => {
        rfRef.current = instance;
        requestAnimationFrame(() => instance.fitView({ padding: 0.28, duration: 0, maxZoom: 1.05 }));
    }, []);

    // klik poza menu zamyka
    useEffect(() => {
        if (!menu.open) return;
        const onDown = () => closeMenu();
        window.addEventListener("pointerdown", onDown);
        return () => window.removeEventListener("pointerdown", onDown);
    }, [menu.open, closeMenu]);

    // otwierasz menu => dociągnij listę
    useEffect(() => {
        if (!menu.open) return;
        loadPicker(pickerCategory);
    }, [menu.open, loadPicker, pickerCategory]);

    // ====== PICK ITEM => add NODE + add EDGE relation ======
    const pickItem = useCallback(
        (item) => {
            const guid = normalizeId(item.guid ?? item.id);
            if (!guid) return;

            const entryNode = {
                id: guid,
                label: item.name ?? "Unnamed",
                category: item.category ?? pickerCategory,
                // zostawiamy możliwość późniejszego mapowania do final JSON:
                data: item.data ?? "{}",
            //    instruction: item.instruction ?? "",
            };

            setFlowData((prev) => {
                const next = ensureFlowShape(deepClone(prev ?? {}));

                const parentId = menu.mode === "root" ? "Agent" : normalizeId(menu.parentNodeId);


                // RULE: nie można dodać samego siebie
                if (normalizeId(parentId) === normalizeId(guid)) return next;

                // ensure node
                upsertNode(next, entryNode);

                // ensure edge parent -> node
                const eId = `e-${parentId}->${guid}`;
                upsertEdge(next, {
                    id: eId,
                    source: parentId,
                    target: guid,
                    relation: "related",
                    bidirectional: true,
                });

                // focus stays on parent (albo możesz ustawić na guid)
                next.ui = next.ui ?? {};
                next.ui.focusNodeId = parentId;

                return next;
            });

            closeMenu();
        },
        [menu.mode, menu.parentNodeId, pickerCategory, closeMenu, setFlowData]
    );

    // filtrowanie listy w menu (child => bez ROOT) + forbidden (self + already used)
    const visiblePickerResults = useMemo(() => {
        if (!menu.open) return [];
        const list = pickerResults ?? [];
        const forbidden = forbiddenForGraph(draft, menu);

        return list.filter((x) => {
            const guid = normalizeId(x.guid ?? x.id);


            // forbidden (self / already used)
            if (forbidden.has(guid)) return false;

            return true;
        });
    }, [menu.open, menu.mode, pickerResults, draft, menu]);

    // --- state ---

    // --- tiny inline icons ---
    const InfoIcon = (props) => (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" {...props}>
            <path
                d="M12 17v-6"
                stroke="currentColor"
                strokeWidth="2.1"
                strokeLinecap="round"
            />
            <path
                d="M12 7.2h.01"
                stroke="currentColor"
                strokeWidth="3.2"
                strokeLinecap="round"
            />
            <path
                d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                stroke="currentColor"
                strokeWidth="2.1"
            />
        </svg>
    );

    function ModeSwitch({ value, onChange }) {
        const [hover, setHover] = useState(false);

        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 999,
                    border: "1px solid rgba(17,24,39,0.14)",
                    background:
                        "linear-gradient(180deg, rgba(255,255,255,0.94), rgba(255,255,255,0.82))",
                    boxShadow: "0 12px 30px rgba(0,0,0,0.14)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    position: "relative",
                    userSelect: "none",
                }}
            >
                <button
                    onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        try {
                            let defaultContent = {
                                "nodes": [
                                    {
                                        "id": "start",
                                        "type": "startNode",
                                        "position": {
                                            "x": 400,
                                            "y": 250
                                        },
                                        "deletable": false,
                                        "data": {
                                            "label": "Start",
                                            "function_name": "Start",
                                            "parameters": [],
                                            "description": ""
                                        },
                                        "draggable": true,
                                        "width": 120,
                                        "height": 60,
                                        "selected": false,
                                        "selectable": false,
                                        "positionAbsolute": {
                                            "x": 750,
                                            "y": 200.
                                        },
                                        "dragging": false
                                    },
                                    {
                                        "id": "end",
                                        "type": "endNode",
                                        "position": {
                                            "x": 800,
                                            "y": 250
                                        },
                                        "deletable": false,
                                        "data": {
                                            "label": "End",
                                            "function_name": "End",
                                            "parameters": [],
                                            "description": ""
                                        },
                                        "draggable": true,
                                        "width": 120,
                                        "height": 60,
                                        "selected": false,
                                        "selectable": false,
                                        "positionAbsolute": {
                                            "x": 740.1257325320832,
                                            "y": 685.16062875586005
                                        },
                                        "dragging": false
                                    }
                                ],
                                "edges": [

                                ],
                                "viewport": {
                                    "x": -1204.377197596249,
                                    "y": -361.9509860969828,
                                    "zoom": 2
                                }
                            }
                            startAiOverlay("Generating…");

                            // przykładowy request
                            const resp = await fetch("api/SkillMapProxy/generate", {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    Authorization: "Bearer " + token,
                                },
                                body: JSON.stringify({
                                    jobDescription,
                                     mode: aiMode
                                    // skills: możesz dodać jeśli kiedyś chcesz
                                }),
                            });

                            const txt = await resp.text();
                            console.log("AI RAW RESPONSE:", txt);

                            if (!resp.ok) throw new Error(`AI failed: ${resp.status}`);

                            // AI powinno zwrócić JSON flowData
                            const parsed = JSON.parse(txt);

                            if (Array.isArray(parsed?.nodes)) {
                                parsed.nodes = parsed.nodes.map((n) => ({
                                    ...n,
                                    data: aiMode === "draft" ? {description: n["description"], ...defaultContent } : n.data, 
                                }));
                            }



                            setFlowData(parsed);
                            console.log("AI PARSED FLOW:", parsed);


                            stopAiOverlay("success");
                            if (aiMode === "draft") {
                                setDraftSaved(false);
                            }
                            
                        } catch (err) {
                            console.error(err);
                            stopAiOverlay("error", "Generation failed");
                            // możesz dodać toast
                            setTimeout(() => setAiGen((s) => ({ ...s, open: false })), 700);
                        }
                    }}
                    style={{
                        zIndex: 85,
                        height: 34,
                        padding: "0 14px",
                        borderRadius: 999,
                        border: "1px solid rgba(119,76,188,0.22)",
                        background:
                            "linear-gradient(55deg, rgba(200,20,241,0.95), rgba(119,50,200,0.95), rgba(90,20,200,0.95))",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                        cursor: "pointer",
                        fontWeight: 950,
                        fontSize: 12,
                        color: "#fff",
                        letterSpacing: 0.2,
                    }}
                    title="Generate graph using AI"
                >
                    Generate with AI
                </button>

                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 950,
                        letterSpacing: 0.28,
                        color: "rgba(15,23,42,0.78)",
                        textTransform: "uppercase",
                        paddingLeft: 6,
                    }}
                >
                    AI mode
                </div>

                {/* segmented switch */}
                <div
                    style={{
                        position: "relative",
                        width: 168,
                        height: 34,
                        borderRadius: 999,
                        border: "1px solid rgba(17,24,39,0.12)",
                        background: "rgba(15,23,42,0.04)",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    {/* sliding pill */}
                    <div
                        style={{
                            position: "absolute",
                            top: 2,
                            bottom: 2,
                            left: value === "live" ? 2 : 84,
                            width: 82,
                            borderRadius: 999,
                            background:
                                "linear-gradient(135deg, rgba(99,102,241,0.95), rgba(119,76,188,0.95), rgba(147,51,234,0.95))",
                            boxShadow: "0 12px 26px rgba(17,24,39,0.20)",
                            transition: "left 220ms cubic-bezier(.2,.8,.2,1)",
                        }}
                    />

                    <button
                        type="button"
                        onClick={() => onChange("live")}
                        style={{
                            width: "50%",
                            height: "100%",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            position: "relative",
                            zIndex: 2,
                            fontSize: 12,
                            fontWeight: 950,
                            letterSpacing: 0.2,
                            color: value === "live" ? "rgba(255,255,255,0.98)" : "rgba(15,23,42,0.70)",
                            transition: "color 160ms ease",
                        }}
                    >
                        Live
                    </button>

                    <button
                        type="button"
                        onClick={() => onChange("draft")}
                        style={{
                            width: "50%",
                            height: "100%",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            position: "relative",
                            zIndex: 2,
                            fontSize: 12,
                            fontWeight: 950,
                            letterSpacing: 0.2,
                            color:
                                value === "draft" ? "rgba(255,255,255,0.98)" : "rgba(15,23,42,0.70)",
                            transition: "color 160ms ease",
                        }}
                    >
                        Draft
                    </button>
                </div>

                {!draftSaved && < button
                    onClick={async (e) => {
                        const skillsNodes = flowData?.nodes ?? flowData ?? [];
                        const resp = await fetch("api/SkillMapProxy/savedrafts", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: "Bearer " + token,
                            },
                            body: JSON.stringify({
                                jobDescription,
                                mode: aiMode,
                                skills: skillsNodes
                            }),
                        });

                        setDraftSaved(true);
                    }}
                    style={{
                        zIndex: 85,
                        height: 34,
                        padding: "0 14px",
                        borderRadius: 999,
                        border: "1px solid rgba(119,76,188,0.22)",
                        background:
                            "linear-gradient(55deg, rgba(0,200,241,0.95), rgba(0,100,200,0.95), rgba(90,20,200,0.95))",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                        cursor: "pointer",
                        fontWeight: 950,
                        fontSize: 12,
                        color: "#fff",
                        letterSpacing: 0.2,
                    }}
                    title="Generate graph using AI"
                >
                    Save Drafts
                </button>}

                {/* tooltip */}
                <div
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    style={{ position: "relative", display: "flex", alignItems: "center" }}
                >
                    <div
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: 999,
                            border: "1px solid rgba(17,24,39,0.12)",
                            background:
                                hover
                                    ? "linear-gradient(180deg, rgba(119,76,188,0.10), rgba(255,255,255,0.95))"
                                    : "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.82))",
                            boxShadow: "0 12px 26px rgba(17,24,39,0.14)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: hover ? "rgba(119,76,188,0.95)" : "rgba(15,23,42,0.55)",
                            transition: "all 160ms ease",
                        }}
                    >
                        <InfoIcon />
                    </div>

                    {hover && (
                        <div
                            style={{
                                position: "absolute",
                                right: 0,
                                top: "calc(100% + 10px)",
                                width: 360,
                                padding: "12px 12px",
                                borderRadius: 14,
                                border: "1px solid rgba(17,24,39,0.14)",
                                background:
                                    "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,255,255,0.86))",
                                boxShadow: "0 26px 70px rgba(0,0,0,0.22)",
                                color: "rgba(15,23,42,0.78)",
                                fontSize: 12.5,
                                lineHeight: 1.35,
                                zIndex: 200,
                            }}
                        >
                            <div style={{ fontWeight: 950, color: "rgba(15,23,42,0.86)", marginBottom: 6 }}>
                                Live vs Draft
                            </div>
                            <div style={{ marginBottom: 8 }}>
                                <span style={{ fontWeight: 900 }}>Live</span>: graph is built only from the
                                <span style={{ fontWeight: 900 }}> existing skills</span>.
                            </div>
                            <div>
                                <span style={{ fontWeight: 900 }}>Draft</span>: the model will propose
                                <span style={{ fontWeight: 900 }}> new skills</span> needed by the job description, once saved they will appear in Resources > Skills
                                
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }


    return (
        <div
            style={{
                position: "relative",
                inset: 0,
                zIndex: 50,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 14,
            }}
        >
      

            <div
                ref={containerRef}
                style={{
                    width: "95vw",
                    height: "60vh",
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(10px)",
                    borderRadius: 14,
                    border: "1px solid rgba(17,24,39,0.18)",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
                }}
            >
                <div style={{ position: "absolute", top: 10, left: 14, zIndex: 90 }}>
                    <ModeSwitch value={aiMode} onChange={setAiMode} />
                </div>

                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clearAll();
                        setDraftSaved(true);
                    }}
                    style={{
                        position: "absolute",
                        bottom: 10,
                        right: 14,              // albo right: 54 jeśli chcesz obok X
                        zIndex: 85,
                        height: 34,
                        padding: "0 12px",
                        borderRadius: 999,
                        border: "1px solid rgba(17,24,39,0.14)",
                        background: "rgba(255,255,255,0.92)",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                        cursor: "pointer",
                        fontWeight: 900,
                        fontSize: 12,
                        color: "#111827",
                        letterSpacing: 0.2,
                        opacity:0.5
                    }}
                    title="Clear all nodes and relations"
                >
                    Clear all
                </button>

                {/* close */}
                {onClose && (
                    <button
                        onClick={onClose}
                        style={{
                            position: "absolute",
                            top: 10,
                            right: 14,
                            zIndex: 80,
                            fontSize: 26,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: "#374151",
                        }}
                    >
                        ×
                    </button>
                )}

                {/* menu przy plusiku */}
                {menu.open && (
                    <div
                        onPointerDown={(e) => {
                            e.stopPropagation();
                        }}
                        style={{
                            position: "absolute",
                            zIndex: 90,
                            left: menu.x,
                            top: menu.y,
                            transform: "translate(-10px, 10px)",
                            width: 360,
                            background: "#fff",
                            borderRadius: 12,
                            border: "1px solid rgba(17,24,39,0.12)",
                            boxShadow: "0 28px 70px rgba(0,0,0,0.25)",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                padding: "10px 12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderBottom: "1px solid rgba(17,24,39,0.08)",
                                gap: 10,
                            }}
                        >
                            <div style={{ fontWeight: 900, color: "#111827" }}>
                                Add {menu.mode === "root" ? "ROOT relation" : "relation"}
                            </div>

                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <select
                                    value={pickerCategory}
                                    onChange={(e) => setPickerCategory(e.target.value)}
                                    style={{
                                        borderRadius: 10,
                                        border: "1px solid rgba(17,24,39,0.12)",
                                        padding: "6px 10px",
                                        fontSize: 13,
                                    }}
                                >
                                    <option value="skill">skill</option>
                                    <option value="process">process</option>
                                </select>

                                <button
                                    onClick={closeMenu}
                                    style={{
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        fontSize: 18,
                                        color: "#6b7280",
                                        padding: 0,
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div style={{ maxHeight: 340, overflow: "auto" }}>
                            {pickerLoading ? (
                                <div style={{ padding: 12, color: "#6b7280", fontWeight: 700 }}>
                                    Loading…
                                </div>
                            ) : visiblePickerResults.length === 0 ? (
                                <div style={{ padding: 12, color: "#6b7280", fontWeight: 700 }}>
                                    No items.
                                </div>
                            ) : (
                                visiblePickerResults.map((r) => (
                                    <button
                                        key={r.guid ?? r.id}
                                        onClick={() => pickItem(r)}
                                        style={{
                                            width: "100%",
                                            textAlign: "left",
                                            padding: "10px 12px",
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            borderBottom: "1px solid rgba(17,24,39,0.06)",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.background = "rgba(17,24,39,0.03)")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.background = "transparent")
                                        }
                                    >
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                            <div style={{ fontWeight: 900, color: "#111827" }}>{r.name}</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                                            {r.guid ?? r.id}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
                {details.open && (
                    <ReadOnlyWorkflowViewer flowJsonString={details.flowJson} onClose={() => setDetails({ open: false })} />

                )}

                <BubbleCloudOverlay open={aiGen.open} text={aiGen.message} />

                {/* Flow */}
                <div style={{ width: "100%", height: "100%" }}>
                    <ReactFlow
                        proOptions={proOptions}
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onInit={onInit}
                        onNodesChange={() => { }}
                        onEdgesChange={() => { }}
                        fitView
                        fitViewOptions={{ padding: 0.28, maxZoom: 1.05 }}
                        minZoom={0.18}
                        maxZoom={1.2}

                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        panOnDrag={true}
                        zoomOnScroll={true}
                        zoomOnPinch={true}
                    >
                        <Background variant="dots" gap={18} size={1} color="rgba(119,76,188,0.10)" />


                        {/* Controls możesz zostawić albo wywalić — jak wolisz */}
                        {/* <Controls /> */}
                    </ReactFlow>
                </div>
            </div>
        </div>
    );
}
